import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { setYear } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import DuplicateContactWarning from "@/components/DuplicateContactWarning";
import { findSimilarContacts, upgradeBirthdayMatches, type DuplicateMatch } from "@/lib/duplicate-detection";
import AddOccasionSheet, { type OccasionFormData } from "@/components/AddOccasionSheet";
import OccasionRow from "@/components/OccasionRow";
import NewBabyPrompt from "@/components/NewBabyPrompt";
import AboutScreen from "@/components/AboutScreen";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";
import DatePickerField from "@/components/ui/date-picker-field";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";

type Contact = Tables<"contacts">;

interface Occasion {
  id?: string;
  occasion_type: OccasionType;
  label: string | null;
  date: string;
  is_recurring: boolean;
  converted_to_contact: boolean;
  babyName?: string;
  isNew?: boolean;
  reminder_lead_time?: string | null;
}

const RELATIONSHIPS = ["Partner", "Parent", "Child", "Sibling", "Friend", "Colleague", "Other"];

interface AddEditPersonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: Contact | null;
  onSaved: () => void;
  existingContacts?: Contact[];
  onViewContact?: (contact: Contact) => void;
  onBabyPrompt?: (data: { babyName: string; date: string; contactName: string; atContactLimit: boolean; occasionDate: string }) => void;
  convertedBabyDate?: string | null;
  onConvertedBabyDateHandled?: () => void;
}

const AddEditPerson = ({ open, onOpenChange, contact, onSaved, existingContacts = [], onViewContact, onBabyPrompt, convertedBabyDate, onConvertedBabyDateHandled }: AddEditPersonProps) => {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const [fullName, setFullName] = useState("");
  const [relationship, setRelationship] = useState("Friend");
  const [birthday, setBirthday] = useState<Date | undefined>();
  const [notes, setNotes] = useState(""); // kept for reset logic only
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([]);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Occasions state
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [occasionSheetOpen, setOccasionSheetOpen] = useState(false);
  const [editingOccasionIndex, setEditingOccasionIndex] = useState<number | null>(null);
  const [pendingBabyPrompt, setPendingBabyPrompt] = useState<{ babyName: string; date: string } | null>(null);
  const [contactCount, setContactCount] = useState(0);

  // About screen state (Fix 1)
  const [aboutOpen, setAboutOpen] = useState(false);
  
  // Tier gate
  const { isAtContactLimit } = useTierGate();
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Unsaved changes guard state (Fix 5)
  const [showExitGuard, setShowExitGuard] = useState(false);
  const [initialValues, setInitialValues] = useState<{
    fullName: string;
    relationship: string;
    birthday: string | undefined;
    occasionIds: string;
  } | null>(null);

  const isEditing = !!contact;

  useEffect(() => {
    if (contact) {
      setFullName(contact.full_name);
      setRelationship(contact.relationship);
      setBirthday(new Date(contact.birthday + "T00:00:00"));
      setNotes(contact.notes ?? "");
      fetchOccasions(contact.id);
    } else {
      setFullName("");
      setRelationship("Friend");
      setBirthday(undefined);
      setNotes("");
      setOccasions([]);
    }
    setDuplicateMatches([]);
    setWarningDismissed(false);
    setShowExitGuard(false);
  }, [contact, open]);

  // Capture initial values after load for dirty check (edit mode only)
  useEffect(() => {
    if (open && isEditing && contact) {
      // Delay slightly to let occasions load
      const timer = setTimeout(() => {
        setInitialValues({
          fullName: contact.full_name,
          relationship: contact.relationship,
          birthday: contact.birthday,
          occasionIds: JSON.stringify(occasions.map(o => ({ id: o.id, type: o.occasion_type, date: o.date, label: o.label }))),
        });
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setInitialValues(null);
    }
  }, [open, isEditing, contact?.id]);

  // Recompute initial occasion snapshot after occasions load
  useEffect(() => {
    if (open && isEditing && contact && initialValues && !initialValues.occasionIds) {
      setInitialValues(prev => prev ? {
        ...prev,
        occasionIds: JSON.stringify(occasions.map(o => ({ id: o.id, type: o.occasion_type, date: o.date, label: o.label }))),
      } : null);
    }
  }, [occasions]);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !initialValues) return false;
    const currentBirthday = birthday ? format(setYear(birthday, 2000), "yyyy-MM-dd") : undefined;
    const currentOccasionIds = JSON.stringify(occasions.map(o => ({ id: o.id, type: o.occasion_type, date: o.date, label: o.label })));
    return (
      fullName.trim() !== initialValues.fullName ||
      relationship !== initialValues.relationship ||
      currentBirthday !== initialValues.birthday ||
      currentOccasionIds !== initialValues.occasionIds
    );
  }, [fullName, relationship, birthday, occasions, initialValues, isEditing]);

  const handleGuardedClose = useCallback((newOpen: boolean) => {
    if (!newOpen && isEditing && hasUnsavedChanges) {
      setShowExitGuard(true);
      return;
    }
    onOpenChange(newOpen);
  }, [isEditing, hasUnsavedChanges, onOpenChange]);

  useEffect(() => {
    if (user) {
      supabase
        .from("contacts")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("locked", false)
        .then(({ count }) => setContactCount(count ?? 0));
    }
  }, [user, open]);

  const fetchOccasions = async (contactId: string) => {
    const { data } = await supabase
      .from("occasions")
      .select("*")
      .eq("contact_id", contactId)
      .order("date", { ascending: true });
    if (data) {
      setOccasions(data.map((o: any) => ({
        id: o.id,
        occasion_type: o.occasion_type as OccasionType,
        label: o.label,
        date: o.date,
        is_recurring: o.is_recurring,
        converted_to_contact: o.converted_to_contact,
        reminder_lead_time: o.reminder_lead_time ?? null,
      })));
    }
  };

  // Mark new_baby occasion as converted when baby is added from Dashboard prompt
  useEffect(() => {
    if (!convertedBabyDate) return;
    setOccasions((prev) =>
      prev.map((o) =>
        o.occasion_type === "new_baby" && o.date === convertedBabyDate
          ? { ...o, converted_to_contact: true }
          : o
      )
    );
    onConvertedBabyDateHandled?.();
  }, [convertedBabyDate]);

  // Upgrade matches when birthday changes
  useEffect(() => {
    if (duplicateMatches.length === 0 || warningDismissed) return;
    const birthdayStr = birthday ? format(setYear(birthday, 2000), "yyyy-MM-dd") : undefined;
    const upgraded = upgradeBirthdayMatches(duplicateMatches, birthdayStr);
    const changed = upgraded.some((m, i) => m.confidence !== duplicateMatches[i].confidence);
    if (changed) setDuplicateMatches(upgraded);
  }, [birthday]);

  const handleNameBlur = () => {
    if (isEditing) return;
    if (existingContacts.length === 0 || warningDismissed) return;
    const trimmed = fullName.trim();
    if (trimmed.length < 3) {
      setDuplicateMatches([]);
      return;
    }
    let matches = findSimilarContacts(trimmed, existingContacts, isEditing ? contact?.id : undefined);
    if (birthday) {
      const birthdayStr = format(setYear(birthday, 2000), "yyyy-MM-dd");
      matches = upgradeBirthdayMatches(matches, birthdayStr);
    }
    setDuplicateMatches(matches);
  };

  const handleContinue = () => {
    setWarningDismissed(true);
    setDuplicateMatches([]);
  };

  const handleGoBack = () => {
    setFullName("");
    setDuplicateMatches([]);
    setWarningDismissed(false);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleViewContact = (c: Contact) => {
    onViewContact?.(c);
  };

  const handleAddOccasion = (data: OccasionFormData) => {
    if (editingOccasionIndex !== null) {
      setOccasions((prev) =>
        prev.map((o, i) =>
          i === editingOccasionIndex
            ? { ...o, occasion_type: data.occasionType, date: data.date, label: data.label, is_recurring: data.isRecurring, babyName: data.babyName, reminder_lead_time: data.reminderLeadTime ?? null }
            : o
        )
      );
      setEditingOccasionIndex(null);
    } else {
      const newOccasion: Occasion = {
        occasion_type: data.occasionType,
        date: data.date,
        label: data.label,
        is_recurring: data.isRecurring,
        converted_to_contact: false,
        babyName: data.babyName,
        isNew: true,
        reminder_lead_time: data.reminderLeadTime ?? null,
      };
      setOccasions((prev) => [...prev, newOccasion]);

      if (data.occasionType === "new_baby") {
        if (onBabyPrompt) {
          setTimeout(() => {
            onBabyPrompt({ babyName: data.babyName || "", date: data.date, contactName: fullName, atContactLimit: isAtContactLimit(contactCount), occasionDate: data.date });
          }, 400);
        } else {
          setTimeout(() => {
            setPendingBabyPrompt({ babyName: data.babyName || "", date: data.date });
          }, 400);
        }
      }
    }
  };

  const handleDeleteOccasion = async (index: number) => {
    const occ = occasions[index];
    if (!confirm("Remove this occasion?")) return;
    if (occ.id) {
      const { error } = await supabase.from("occasions").delete().eq("id", occ.id);
      if (error) {
        toast.error("That didn't work — give it another go.");
        return;
      }
      // Refresh Dashboard state immediately
      onSaved();
    }
    setOccasions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (afterSaveCallback?: () => void) => {
    if (!user || !fullName.trim() || !birthday) return;
    setSaving(true);

    const payload: any = {
      full_name: fullName.trim().slice(0, 100),
      relationship,
      birthday: format(setYear(birthday, 2000), "yyyy-MM-dd"),
      user_id: user.id,
    };

    // Preserve existing notes on edit, don't include on new contacts
    if (isEditing && contact) {
      // Don't touch notes — they're managed via the About screen now
    }

    // D1: Contact limit gate for new contacts
    if (!isEditing && isAtContactLimit(contactCount)) {
      setSaving(false);
      setPaywallOpen(true);
      return;
    }

    let error;
    let contactId = contact?.id;

    if (isEditing && contact) {
      ({ error } = await supabase
        .from("contacts")
        .update(payload)
        .eq("id", contact.id));
    } else {
      const { data: newContact, error: insertError } = await supabase
        .from("contacts")
        .insert(payload)
        .select("id")
        .single();
      error = insertError;
      contactId = newContact?.id;

      // Update last_active_at on contact added
      if (!insertError && user) {
        supabase
          .from("profiles")
          .update({ last_active_at: new Date().toISOString() } as any)
          .eq("user_id", user.id)
          .then(() => {});
      }
    }

    if (error) {
      setSaving(false);
      if (error.message?.includes("Contact limit reached")) {
        toast.error("You've reached the contact limit. Upgrade to Dearestly Plus for unlimited contacts.");
      } else {
        toast.error("Something went wrong — please try again.");
      }
      return;
    }

    // Save new occasions
    if (contactId) {
      let occasionError = false;

      // Birthday occasion is auto-created by the database trigger (sync_contact_birthday_to_occasion)
      // so we only need to save non-birthday new occasions here.
      const newOccasions = occasions.filter((o) => o.isNew);
      if (newOccasions.length > 0) {
        const inserts = newOccasions.map((o) => ({
          contact_id: contactId!,
          user_id: user.id,
          occasion_type: o.occasion_type,
          label: o.label,
          date: o.date,
          is_recurring: o.is_recurring,
          converted_to_contact: o.converted_to_contact,
          reminder_lead_time: o.reminder_lead_time ?? null,
        }));
        const { error: insertErr } = await supabase.from("occasions").insert(inserts);
        if (insertErr) {
          console.error("Failed to save occasions:", insertErr);
          occasionError = true;
        } else if (user) {
          // Update last_active_at on occasion added
          supabase
            .from("profiles")
            .update({ last_active_at: new Date().toISOString() } as any)
            .eq("user_id", user.id)
            .then(() => {});
        }
      }

      // Update existing occasions that were edited
      for (const occ of occasions) {
        if (occ.id && !occ.isNew) {
          const { error: updateErr } = await supabase
            .from("occasions")
            .update({
              occasion_type: occ.occasion_type,
              date: occ.date,
              label: occ.label,
              is_recurring: occ.is_recurring,
              converted_to_contact: occ.converted_to_contact,
              reminder_lead_time: occ.reminder_lead_time ?? null,
            })
            .eq("id", occ.id);
          if (updateErr) {
            console.error("Failed to update occasion:", updateErr);
            occasionError = true;
          }
        }
      }

      if (occasionError) {
        toast.error("Contact saved but some occasions couldn't be saved. Please try editing again.");
        setSaving(false);
        onOpenChange(false);
        onSaved();
        afterSaveCallback?.();
        return;
      }
    }

    setSaving(false);

    // Success delight moment for new contacts only
    if (!isEditing) {
      setShowSuccess(true);
      await new Promise((r) => setTimeout(r, 900));
      setShowSuccess(false);
      onOpenChange(false);
      onSaved();
      afterSaveCallback?.();
    } else {
      toast.success(`${fullName.trim()} updated!`);
      onOpenChange(false);
      onSaved();
      afterSaveCallback?.();
    }
  };

  const existingOccasionTypes = [
    ...(birthday ? ["birthday" as OccasionType] : []),
    ...occasions.map((o) => o.occasion_type as OccasionType),
  ];
  const showWarning = duplicateMatches.length > 0 && !warningDismissed;

  const firstName = fullName.trim().split(" ")[0] || "them";

  return (
    <>
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={reduce ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background px-6 text-center"
          >
            <motion.div
              initial={reduce ? false : { scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ backgroundColor: "#C9A84C" }}
            >
              <Check className="h-10 w-10 text-white" strokeWidth={3} />
            </motion.div>
            <p className="mb-2 font-serif text-2xl font-semibold text-foreground">
              {fullName.trim()} added.
            </p>
            <p className="text-body text-muted-foreground">
              One more person you'll always show up for.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <Sheet open={open} onOpenChange={handleGuardedClose}>
        <SheetContent className="overflow-y-auto bg-background sm:max-w-md">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl">
              {isEditing ? "Edit Person" : "Add Person"}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-5">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                ref={nameInputRef}
                placeholder="e.g. Marcus Johnson"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={handleNameBlur}
                maxLength={100}
                className="rounded-full"
                autoFocus
              />
              {showWarning && (
                <DuplicateContactWarning
                  matches={duplicateMatches}
                  onContinue={handleContinue}
                  onViewContact={handleViewContact}
                  onGoBack={handleGoBack}
                />
              )}
            </div>

            {/* Relationship */}
            <div className="space-y-2">
              <Label>Relationship</Label>
              <Select value={relationship} onValueChange={setRelationship}>
                <SelectTrigger className="rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label>Birthday</Label>
              <DatePickerField
                value={birthday}
                onChange={(date) => setBirthday(date)}
                placeholder="Select a date"
                title="Birthday"
                yearOptional
              />
            </div>

            {/* About entry point — edit mode only */}
            {isEditing && contact && (
              <button
                onClick={() => setAboutOpen(true)}
                className="flex w-full items-center justify-between rounded-2xl bg-card p-4 shadow-sm text-left"
              >
                <div>
                  <p className="text-sm font-bold text-foreground">About</p>
                  <p className="text-xs text-muted-foreground">Personality, loves, dislikes & notes</p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-accent" />
              </button>
            )}

            {/* Occasions */}
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-bold text-foreground">Occasions</Label>
                <p className="text-xs text-muted-foreground">Add important dates beyond their birthday</p>
              </div>

              {occasions.length > 0 && (
                <div className="space-y-1">
                  {occasions.map((occ, i) => (
                    <div key={occ.id || i} className="flex items-center gap-1">
                      <div className="flex-1 min-w-0">
                        <OccasionRow
                          occasionType={occ.occasion_type}
                          label={occ.label}
                          date={occ.date}
                          isRecurring={occ.is_recurring}
                          onTap={() => {
                            setEditingOccasionIndex(i);
                            setOccasionSheetOpen(true);
                          }}
                        />
                      </div>
                      <button
                        onClick={() => handleDeleteOccasion(i)}
                        className="p-1 text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Occasions are removed immediately when deleted.
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setEditingOccasionIndex(null);
                  setOccasionSheetOpen(true);
                }}
                className="flex items-center gap-1 text-sm font-medium text-accent hover:underline"
              >
                <Plus className="h-3.5 w-3.5" />
                Add occasion
              </button>
            </div>

            {/* Save */}
            <Button
              onClick={() => handleSave()}
              disabled={saving || !fullName.trim() || !birthday}
              className="w-full rounded-full"
              size="lg"
            >
              {saving
                ? "Saving…"
                : isEditing
                ? "Save Changes"
                : "Add to Circle"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* About screen overlay (Fix 1) */}
      {isEditing && contact && (
        <AboutScreen
          open={aboutOpen}
          onOpenChange={setAboutOpen}
          contact={contact}
          onSaved={() => {
            // Re-fetch contact data but don't close Edit Person
            onSaved();
          }}
        />
      )}

      {/* Unsaved changes exit guard dialog (Fix 5) */}
      <Dialog open={showExitGuard} onOpenChange={setShowExitGuard}>
        <DialogContent className="rounded-2xl p-6 sm:max-w-sm [&>button]:hidden">
          <DialogTitle className="text-lg font-bold text-foreground">
            You have unsaved changes
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-2">
            Would you like to save your changes to {firstName} before leaving?
          </DialogDescription>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => {
                setShowExitGuard(false);
                handleSave(() => {});
              }}
              className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"
              size="lg"
            >
              Save Changes
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowExitGuard(false);
                onOpenChange(false);
              }}
              className="w-full rounded-full"
              size="lg"
            >
              Discard Changes
            </Button>
            <button
              onClick={() => setShowExitGuard(false)}
              className="text-sm font-medium text-accent text-center py-2"
            >
              Keep Editing
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AddOccasionSheet
        open={occasionSheetOpen}
        onOpenChange={setOccasionSheetOpen}
        onSave={handleAddOccasion}
        existingOccasionTypes={existingOccasionTypes}
        editingOccasion={
          editingOccasionIndex !== null
            ? {
                occasionType: occasions[editingOccasionIndex].occasion_type,
                date: occasions[editingOccasionIndex].date,
                label: occasions[editingOccasionIndex].label,
                isRecurring: occasions[editingOccasionIndex].is_recurring,
                reminderLeadTime: occasions[editingOccasionIndex].reminder_lead_time ?? null,
              }
            : null
        }
      />

      {!onBabyPrompt && pendingBabyPrompt && (
        <NewBabyPrompt
          open={!!pendingBabyPrompt}
          onOpenChange={(v) => { if (!v) setPendingBabyPrompt(null); }}
          babyName={pendingBabyPrompt.babyName}
          occasionDate={pendingBabyPrompt.date}
          contactName={fullName}
          atContactLimit={isAtContactLimit(contactCount)}
          onAddToCircle={(prefillData) => {
            setPendingBabyPrompt(null);
            setOccasions((prev) =>
              prev.map((o) =>
                o.occasion_type === "new_baby" && o.date === pendingBabyPrompt.date
                  ? { ...o, converted_to_contact: true }
                  : o
              )
            );
            handleSave();
          }}
          onDismiss={() => setPendingBabyPrompt(null)}
        />
      )}

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title="Unlimited contacts is a Dearestly Plus feature"
        benefit="Add everyone who matters — no limits on the people in your circle."
      />
    </>
  );
};

export default AddEditPerson;
